import { useState, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useListEstimates, useUpdateEstimate, useGetSettings, useDuplicateEstimate, useDeleteEstimate, useConvertEstimateToInvoice, useSendEstimateEmail, useListCustomers, getListEstimatesQueryKey, getListInvoicesQueryKey, getGetSettingsQueryKey, getGetEstimateQueryOptions, useProjectDocumentLinks, useGetQBOBulkSyncStatus, useGetXeroBulkSyncStatus } from "@workspace/api-client-react";
import { QuickBooksSyncBadge } from "@/components/quickbooks-sync-badge";
import { XeroSyncBadge } from "@/components/xero-sync-badge";
import { QuickProjectAssign } from "@/components/quick-project-assign";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Filter, FileText, CheckCircle, Clock, XCircle, Lock, Copy, Trash2, Archive, X, Tag, RefreshCw, Mail, Receipt, Loader2, LayoutTemplate, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useHoverSupported } from "@/hooks/use-hover-supported";
import { exportCustomerPDF, exportInternalPDF, exportBulkZip, previewCustomerPDF, previewInternalPDF, customerPDFBase64, customerPDFFilename } from "@/lib/pdf";
import { useQuickBooksPush } from "@/hooks/use-quickbooks-push";
import { QuickBooksPushMenuItem } from "@/components/quickbooks-push-menu-item";
import { useXeroPush } from "@/hooks/use-xero-push";
import { XeroPushMenuItem } from "@/components/xero-push-menu-item";
import { PDFPreviewDialog } from "@/components/pdf-preview-dialog";
import { MarkAsSentDialog } from "@/components/mark-as-sent-dialog";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { InternalWorkshopView } from "@/components/internal-workshop-view";
import { useTargetMargin, TONE_TEXT } from "@/hooks/use-target-margin";
import { cn } from "@/lib/utils";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";

type EstimateStatus = "draft" | "sent" | "accepted" | "declined" | "converted";
type StatusFilterValue = EstimateStatus | "all";

const STATUS_OPTIONS: { value: EstimateStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "converted", label: "Converted to Invoice" },
];

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
  converted: "Converted to Invoice",
};

const VALID_STATUSES = new Set<string>(["draft", "sent", "accepted", "declined", "converted"]);

function isEstimateStatus(v: string): v is EstimateStatus {
  return VALID_STATUSES.has(v);
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "accepted": return "bg-green-100 text-green-800 border-green-300";
    case "sent": return "bg-blue-100 text-blue-800 border-blue-300";
    case "declined": return "bg-red-100 text-red-800 border-red-300";
    case "converted": return "bg-purple-100 text-purple-800 border-purple-200";
    default: return "bg-slate-100 text-slate-700 border-slate-300";
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "accepted": return <CheckCircle className="w-3.5 h-3.5" />;
    case "sent": return <Clock className="w-3.5 h-3.5" />;
    case "declined": return <XCircle className="w-3.5 h-3.5" />;
    case "converted": return <CheckCircle className="w-3.5 h-3.5" />;
    default: return <FileText className="w-3.5 h-3.5" />;
  }
};

type WorkshopHoverEstimate = {
  profit?: number | null;
  margin?: number | null;
  internalCost?: number | null;
  costBreakdown?: { labour: number; materials: number; fees: number };
  targetMargin?: number | null;
};

type WorkshopHoverCellProps = {
  enabled: boolean;
  estimate: WorkshopHoverEstimate;
  formatCurrency: (value: number) => string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
};

function WorkshopHoverCell({ enabled, estimate, formatCurrency, className, style, onClick, children }: WorkshopHoverCellProps) {
  const { target, defaultTarget } = useTargetMargin(estimate.targetMargin);
  if (!enabled) {
    return (
      <div className={className} style={style} onClick={onClick}>
        {children}
      </div>
    );
  }
  const cb = estimate.costBreakdown;
  const breakdown = cb && (cb.labour > 0 || cb.materials > 0 || cb.fees > 0)
    ? { Labor: cb.labour, Materials: cb.materials, Fees: cb.fees }
    : null;
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className={className} style={style} onClick={onClick}>
          {children}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        side="bottom"
        className="w-72 p-0 border-0 bg-transparent shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        <InternalWorkshopView
          profit={estimate.profit}
          margin={estimate.margin}
          internalCost={estimate.internalCost}
          breakdown={breakdown}
          formatCurrency={formatCurrency}
          enableTooltips={false}
          showFooterHint={false}
          target={target}
          defaultTarget={defaultTarget}
          ownTarget={estimate.targetMargin != null}
        />
      </HoverCardContent>
    </HoverCard>
  );
}

export default function Estimates() {
  const { tone: marginToneOf } = useTargetMargin();
  usePageTitle("Quotes");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("all");
  const [deleteEstimateId, setDeleteEstimateId] = useState<number | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isBulkPushingQbo, setIsBulkPushingQbo] = useState(false);
  const [bulkPushProgress, setBulkPushProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkPushController, setBulkPushController] = useState<AbortController | null>(null);
  const [isBulkPushingXero, setIsBulkPushingXero] = useState(false);
  const [bulkPushProgressXero, setBulkPushProgressXero] = useState<{ current: number; total: number } | null>(null);
  const [bulkPushControllerXero, setBulkPushControllerXero] = useState<AbortController | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [markAsSentOpen, setMarkAsSentOpen] = useState(false);
  const [markAsSentIds, setMarkAsSentIds] = useState<number[]>([]);
  const [sendEmailEstimateId, setSendEmailEstimateId] = useState<number | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [attachServiceAgreement, setAttachServiceAgreement] = useState(true);
  const [convertConfirmId, setConvertConfirmId] = useState<number | null>(null);
  const [fromTemplateOpen, setFromTemplateOpen] = useState(false);
  const [fromTemplateList, setFromTemplateList] = useState<Array<{ id: number; name: string; description: string | null; lineItemCount: number }>>([]);
  const [fromTemplateLoading, setFromTemplateLoading] = useState(false);
  const [fromTemplateSelected, setFromTemplateSelected] = useState<number | null>(null);
  const [ftCustomerId, setFtCustomerId] = useState<number | null>(null);
  const [ftCustomerName, setFtCustomerName] = useState("");
  const [ftCustomerSearch, setFtCustomerSearch] = useState("");
  const [ftCustomerPickerOpen, setFtCustomerPickerOpen] = useState(false);
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;
  const hoverSupported = useHoverSupported();
  const enableHoverWorkshop = isDesktop && hoverSupported;


  const listStatus = statusFilter !== "all" ? statusFilter : undefined;
  const debouncedSearch = useDebouncedValue(search);

  const { data: estimates, isLoading } = useListEstimates({
    search: debouncedSearch || undefined,
    status: listStatus,
  });

  const { data: projectLinks } = useProjectDocumentLinks();

  const { data: companySettings } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() }
  });

  const { user } = useAuth();
  const { hasProAccess: isEmailPro } = getTrialInfo(user);

  const updateEstimate = useUpdateEstimate();
  const duplicateEstimate = useDuplicateEstimate();
  const deleteEstimate = useDeleteEstimate();
  const qboPush = useQuickBooksPush("estimate");
  const xeroPush = useXeroPush("estimate");
  const estimateIdsForQbo = useMemo(
    () => (estimates ?? []).map(e => e.id),
    [estimates],
  );
  const { data: qboBulk } = useGetQBOBulkSyncStatus("estimate", estimateIdsForQbo, {
    enabled: qboPush.connected && estimateIdsForQbo.length > 0,
  });
  const qboStatuses = qboBulk?.statuses;
  const { data: xeroBulk } = useGetXeroBulkSyncStatus("estimate", estimateIdsForQbo, {
    enabled: xeroPush.connected && estimateIdsForQbo.length > 0,
  });
  const xeroStatuses = xeroBulk?.statuses;
  const convertToInvoice = useConvertEstimateToInvoice();
  const sendEmailMutation = useSendEstimateEmail();
  const { data: customers } = useListCustomers();

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  async function openFromTemplate() {
    setFromTemplateSelected(null);
    setFtCustomerId(null);
    setFtCustomerName("");
    setFtCustomerSearch("");
    setFromTemplateLoading(true);
    setFromTemplateOpen(true);
    try {
      const res = await fetch(`${BASE}/api/templates?type=quote`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      setFromTemplateList(await res.json());
    } catch {
      toast({ title: "Failed to load templates", variant: "destructive" });
      setFromTemplateOpen(false);
    } finally {
      setFromTemplateLoading(false);
    }
  }

  async function createFromTemplate() {
    if (!fromTemplateSelected || !ftCustomerId) return;
    setIsCreatingFromTemplate(true);
    try {
      const res = await fetch(`${BASE}/api/templates/${fromTemplateSelected}/create-estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customerId: ftCustomerId }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setFromTemplateOpen(false);
      queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
      setLocation(`/quotes/${data.id}`);
    } catch {
      toast({ title: "Failed to create quote", variant: "destructive" });
    } finally {
      setIsCreatingFromTemplate(false);
    }
  }

  const filteredCustomers = (customers || []).filter(c =>
    c.name.toLowerCase().includes(ftCustomerSearch.toLowerCase())
  );

  const allIds = estimates?.map(e => e.id) ?? [];
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const indeterminate = someSelected && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDuplicate = (id: number) => {
    duplicateEstimate.mutate({ id }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Quote duplicated" });
        setLocation(`/quotes/${result.id}`);
      },
      onError: () => {
        toast({ title: "Failed to duplicate quote", variant: "destructive" });
      }
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteEstimateId === null) return;
    deleteEstimate.mutate({ id: deleteEstimateId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Quote deleted" });
        setDeleteEstimateId(null);
      },
      onError: () => {
        toast({ title: "Failed to delete quote", variant: "destructive" });
        setDeleteEstimateId(null);
      }
    });
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map(id => deleteEstimate.mutateAsync({ id })));
      queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
      toast({ title: `Deleted ${ids.length} quote${ids.length !== 1 ? "s" : ""}` });
      clearSelection();
    } catch {
      toast({ title: "Some deletes failed", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteOpen(false);
    }
  };

  const handleBulkStatusChange = (value: string) => {
    if (!isEstimateStatus(value)) return;
    const ids = Array.from(selectedIds);
    Promise.all(
      ids.map(id => updateEstimate.mutateAsync({ id, data: { status: value } }))
    ).then(() => {
      queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
      toast({ title: `Updated ${ids.length} quote${ids.length !== 1 ? "s" : ""} to ${value}` });
    }).catch(() => {
      toast({ title: "Some updates failed", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
    });
  };

  const { formatCurrency, formatDate } = useSettingsFormat();

  const onStatusFilterChange = (value: string) => {
    if (value === "all" || isEstimateStatus(value)) {
      setStatusFilter(value);
    }
  };

  const onStatusChange = (id: number, value: string) => {
    if (!isEstimateStatus(value)) return;
    updateEstimate.mutate({ id, data: { status: value } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: `Status updated to ${STATUS_LABELS[value] ?? value}` });
      },
      onError: () => {
        toast({ title: "Failed to update status", variant: "destructive" });
      }
    });
  };

  const handleExport = async (id: number, type: "customer" | "internal") => {
    if (type === "internal" && !isEmailPro) { setLocation("/upgrade"); return; }
    try {
      const detail = await queryClient.fetchQuery(getGetEstimateQueryOptions(id));
      const est = {
        ...detail,
        createdAt: String(detail.createdAt),
      };
      if (type === "customer") {
        exportCustomerPDF(est, companySettings ?? {});
        if (!["sent", "accepted", "declined"].includes(detail.status)) {
          setMarkAsSentIds([id]);
          setMarkAsSentOpen(true);
        }
      } else {
        exportInternalPDF(est, companySettings ?? {});
      }
    } catch {
      toast({ title: "Export failed", description: "Could not load quote details.", variant: "destructive" });
    }
  };

  const handlePreview = async (id: number, type: "customer" | "internal") => {
    if (type === "internal" && !isEmailPro) { setLocation("/upgrade"); return; }
    try {
      const detail = await queryClient.fetchQuery(getGetEstimateQueryOptions(id));
      const est = {
        ...detail,
        createdAt: String(detail.createdAt),
      };
      const url = type === "customer"
        ? previewCustomerPDF(est, companySettings ?? {})
        : previewInternalPDF(est, companySettings ?? {});
      setPreviewBlobUrl(url);
      setPreviewTitle(type === "customer" ? "Customer PDF" : "Cost Report PDF");
      setPreviewOpen(true);
    } catch {
      toast({ title: "Preview failed", description: "Could not load quote details.", variant: "destructive" });
    }
  };

  const handleOpenSendEmail = (estimate: { id: number; estimateNumber?: string | null; customerName?: string | null }) => {
    const settings = companySettings;
    setSendEmailEstimateId(estimate.id);
    const applyVars = (template: string, vars: Record<string, string>) =>
      template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
    const vars = {
      customerName: estimate.customerName || "there",
      quoteNumber: estimate.estimateNumber || "",
      invoiceNumber: "",
      companyName: settings?.companyName || "us",
    };
    const defaultSubject = `Quote ${estimate.estimateNumber ? `#${estimate.estimateNumber}` : ""} from ${settings?.companyName || "us"}`;
    const defaultBody = `Hi ${estimate.customerName || "there"},\n\nPlease find your quote attached. Let us know if you have any questions.\n\nThank you for your business!`;
    setEmailSubject(settings?.defaultQuoteEmailSubject ? applyVars(settings.defaultQuoteEmailSubject, vars) : defaultSubject);
    setEmailMessage(settings?.defaultQuoteEmailBody ? applyVars(settings.defaultQuoteEmailBody, vars) : defaultBody);
  };

  const handleConvertConfirm = () => {
    if (convertConfirmId === null) return;
    convertToInvoice.mutate({ id: convertConfirmId }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
        toast({ title: "Invoice created from quote" });
        setConvertConfirmId(null);
        setLocation(`/invoices/${result.id}`);
      },
      onError: () => {
        toast({ title: "Failed to convert quote to invoice", variant: "destructive" });
        setConvertConfirmId(null);
      }
    });
  };

  const handleBulkPushQbo = async () => {
    if (!qboPush.available) {
      qboPush.pushMany([]);
      return;
    }
    const ids = Array.from(selectedIds);
    const controller = new AbortController();
    setBulkPushController(controller);
    setBulkPushProgress({ current: 0, total: ids.length });
    setIsBulkPushingQbo(true);
    try {
      const summary = await qboPush.pushMany(ids, {
        signal: controller.signal,
        onProgress: (p) => setBulkPushProgress({ current: p.current, total: p.total }),
      });
      if (summary && summary.failed === 0 && !summary.cancelled) {
        clearSelection();
      } else if (summary) {
        const remaining = ids.slice(summary.processed);
        const failedIds = new Set<number>([
          ...summary.failures.map(f => f.id),
          ...remaining,
        ]);
        setSelectedIds(failedIds);
      }
    } finally {
      setIsBulkPushingQbo(false);
      setBulkPushProgress(null);
      setBulkPushController(null);
    }
  };

  const cancelBulkPushQbo = () => {
    bulkPushController?.abort();
  };

  const handleBulkPushXero = async () => {
    if (!xeroPush.available) {
      xeroPush.pushMany([]);
      return;
    }
    const ids = Array.from(selectedIds);
    const controller = new AbortController();
    setBulkPushControllerXero(controller);
    setBulkPushProgressXero({ current: 0, total: ids.length });
    setIsBulkPushingXero(true);
    try {
      const summary = await xeroPush.pushMany(ids, {
        signal: controller.signal,
        onProgress: (p) => setBulkPushProgressXero({ current: p.current, total: p.total }),
      });
      if (summary && summary.failed === 0 && !summary.cancelled) {
        clearSelection();
      } else if (summary) {
        const remaining = ids.slice(summary.processed);
        const failedIds = new Set<number>([
          ...summary.failures.map(f => f.id),
          ...remaining,
        ]);
        setSelectedIds(failedIds);
      }
    } finally {
      setIsBulkPushingXero(false);
      setBulkPushProgressXero(null);
      setBulkPushControllerXero(null);
    }
  };

  const cancelBulkPushXero = () => {
    bulkPushControllerXero?.abort();
  };

  const handleBulkExportZip = async (type: "customer" | "internal" | "both") => {
    setZipDialogOpen(false);
    if (type !== "customer" && !isEmailPro) { setLocation("/upgrade"); return; }
    setIsExportingZip(true);
    try {
      const ids = Array.from(selectedIds);
      const details = await Promise.all(
        ids.map(id => queryClient.fetchQuery(getGetEstimateQueryOptions(id)))
      );
      const estimatesData = details.map(detail => ({
        ...detail,
        createdAt: String(detail.createdAt),
      }));
      await exportBulkZip(estimatesData, type, companySettings ?? {});
      toast({ title: `Exported ${ids.length} quote${ids.length !== 1 ? "s" : ""} as ZIP` });
      if (type === "customer" || type === "both") {
        const eligibleIds = details
          .filter(d => !["sent", "accepted", "declined"].includes(d.status))
          .map(d => d.id);
        if (eligibleIds.length > 0) {
          setMarkAsSentIds(eligibleIds);
          setMarkAsSentOpen(true);
        }
      }
      clearSelection();
    } catch {
      toast({ title: "Export failed", description: "Could not export quotes.", variant: "destructive" });
    } finally {
      setIsExportingZip(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className={`flex ${isDesktop ? "flex-row items-center" : "flex-col"} justify-between gap-4`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-8 h-8 text-primary" />
            Quotes
          </h1>
          <p className="text-muted-foreground mt-1">Manage and track your project quotes.</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openFromTemplate}>
            <LayoutTemplate className="w-4 h-4 mr-2" />
            From Template
          </Button>
          <Link href="/quotes/new">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Quote
            </Button>
          </Link>
        </div>
      </div>

      <div className={`flex ${isDesktop ? "flex-row" : "flex-col"} gap-4`}>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by #, customer, or title…"
            className="pl-10 bg-card"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ width: isDesktop ? "12rem" : "100%" }}>
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="bg-card">
              <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk action toolbar */}
      {someSelected && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <span className="text-sm font-medium text-primary mr-1">
            {selectedIds.size} selected
          </span>

          {/* Bulk status change */}
          <Select onValueChange={handleBulkStatusChange}>
            <SelectTrigger className="h-8 w-auto gap-1.5 border-primary/30 text-primary bg-transparent text-xs font-medium px-2.5" style={{ minWidth: "130px" }}>
              <Tag className="w-3.5 h-3.5" />
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.filter(s => s.value !== "converted").map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Bulk export ZIP */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
            onClick={() => setZipDialogOpen(true)}
            disabled={isExportingZip}
          >
            <Archive className="w-3.5 h-3.5" />
            {isExportingZip ? "Exporting…" : "Export ZIP"}
          </Button>

          {/* Bulk push to QuickBooks */}
          {qboPush.connected && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                onClick={handleBulkPushQbo}
                disabled={isBulkPushingQbo || qboPush.needsReconnect}
                data-testid="button-bulk-push-qbo-estimates"
                title={qboPush.needsReconnect ? "Reconnect QuickBooks in Integrations to push." : undefined}
              >
                {isBulkPushingQbo ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] font-bold rounded-sm bg-[#2CA01C] text-white">Q</span>
                )}
                {isBulkPushingQbo
                  ? bulkPushProgress
                    ? `Pushing ${Math.min(bulkPushProgress.current, bulkPushProgress.total)} of ${bulkPushProgress.total}…`
                    : "Pushing…"
                  : "Push to QuickBooks"}
              </Button>
              {isBulkPushingQbo && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={cancelBulkPushQbo}
                  data-testid="button-cancel-bulk-push-qbo-estimates"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              )}
            </>
          )}
          {/* Bulk push to Xero */}
          {xeroPush.connected && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                onClick={handleBulkPushXero}
                disabled={isBulkPushingXero || xeroPush.needsReconnect}
                data-testid="button-bulk-push-xero-estimates"
                title={xeroPush.needsReconnect ? "Reconnect Xero in Integrations to push." : undefined}
              >
                {isBulkPushingXero ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <span className="w-3.5 h-3.5 inline-flex items-center justify-center text-[9px] font-bold rounded-sm bg-[#13B5EA] text-white">X</span>
                )}
                {isBulkPushingXero
                  ? bulkPushProgressXero
                    ? `Pushing ${Math.min(bulkPushProgressXero.current, bulkPushProgressXero.total)} of ${bulkPushProgressXero.total}…`
                    : "Pushing…"
                  : "Push to Xero"}
              </Button>
              {isBulkPushingXero && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={cancelBulkPushXero}
                  data-testid="button-cancel-bulk-push-xero-estimates"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </Button>
              )}
            </>
          )}

          {/* Bulk delete */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 ml-auto text-muted-foreground hover:text-foreground"
            onClick={clearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Card key={i} className="shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/4" />
                </div>
                <div className="space-y-2 flex flex-col items-end">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : estimates?.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-lg border border-dashed">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No quotes found</h3>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            {search || statusFilter !== "all"
              ? "No quotes matched your filters. Try adjusting them."
              : "You haven't created any quotes yet."}
          </p>
          {!search && statusFilter === "all" && (
            <Link href="/quotes/new">
              <Button className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Quote
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border/50 shadow-md overflow-hidden">
          {/* Desktop header row */}
          {isDesktop && (
            <div
              style={{ display: "grid", gridTemplateColumns: "36px 100px 1fr 110px 110px 90px 160px 72px" }}
              className="gap-3 px-4 py-3 border-b bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
            >
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={indeterminate ? "indeterminate" : allSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all quotes"
                />
              </div>
              <div>#</div>
              <div>Customer</div>
              <div style={{ textAlign: "right" }}>Price</div>
              <div className="flex items-center justify-end gap-1">Profit <InfoTip content={FORMULAS.profit} /></div>
              <div className="flex items-center justify-end gap-1">Margin <InfoTip content={FORMULAS.margin} /></div>
              <div style={{ textAlign: "center" }}>Status</div>
              <div style={{ textAlign: "center" }}>Actions</div>
            </div>
          )}

          <div className="divide-y divide-border">
            {estimates?.map((estimate) => (
              <div
                key={estimate.id}
                style={isDesktop ? {
                  display: "grid",
                  gridTemplateColumns: "36px 100px 1fr 110px 110px 90px 160px 72px",
                  alignItems: "center",
                } : { display: "block" }}
                className={`gap-3 px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer ${selectedIds.has(estimate.id) ? "bg-primary/5" : ""}`}
                onClick={() => setLocation(`/quotes/${estimate.id}`)}
              >
                {/* Checkbox (desktop) */}
                {isDesktop && (
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(estimate.id)}
                      onCheckedChange={() => toggleSelect(estimate.id)}
                      aria-label={`Select quote ${estimate.estimateNumber || estimate.id}`}
                    />
                  </div>
                )}

                {/* Estimate # (desktop only) */}
                {isDesktop && (
                  <div
                    className="text-xs font-mono text-muted-foreground cursor-pointer"
                    onClick={() => setLocation(`/quotes/${estimate.id}`)}
                  >
                    {estimate.estimateNumber ? `#${estimate.estimateNumber}` : "—"}
                  </div>
                )}

                {/* Customer */}
                <div
                  className="flex flex-col min-w-0"
                >
                  <div className="font-semibold text-foreground truncate hover:text-primary transition-colors text-sm flex items-center gap-1.5">
                    {estimate.customerName || "—"}
                    {estimate.isRecurring && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded font-medium shrink-0">
                        <RefreshCw className="w-2.5 h-2.5" /> Recurring
                      </span>
                    )}
                    {qboPush.connected && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <QuickBooksSyncBadge kind="estimate" status={qboStatuses?.[String(estimate.id)]} />
                      </span>
                    )}
                    {xeroPush.connected && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <XeroSyncBadge kind="estimate" status={xeroStatuses?.[String(estimate.id)]} />
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {!isDesktop && estimate.estimateNumber && (
                      <span className="font-mono">#{estimate.estimateNumber} ·</span>
                    )}
                    <span>{formatDate(estimate.estimateDate ?? estimate.createdAt)}</span>
                    <QuickProjectAssign kind="quote" id={estimate.id} link={projectLinks?.quotes[String(estimate.id)]} />
                  </div>
                </div>

                {/* Price (desktop only) */}
                {isDesktop && (
                  <WorkshopHoverCell
                    enabled={enableHoverWorkshop}
                    estimate={estimate}
                    formatCurrency={formatCurrency}
                    className="font-semibold text-sm cursor-pointer hover:underline decoration-dotted underline-offset-4"
                    style={{ textAlign: "right" }}
                    onClick={() => setLocation(`/quotes/${estimate.id}`)}
                  >
                    {formatCurrency(estimate.total)}
                  </WorkshopHoverCell>
                )}

                {/* Profit (desktop only) */}
                {isDesktop && (
                  <WorkshopHoverCell
                    enabled={enableHoverWorkshop}
                    estimate={estimate}
                    formatCurrency={formatCurrency}
                    className="text-sm text-muted-foreground cursor-pointer hover:underline decoration-dotted underline-offset-4"
                    style={{ textAlign: "right" }}
                    onClick={() => setLocation(`/quotes/${estimate.id}`)}
                  >
                    {estimate.profit !== undefined && estimate.profit !== null
                      ? formatCurrency(estimate.profit)
                      : <span className="text-muted-foreground/40">—</span>
                    }
                  </WorkshopHoverCell>
                )}

                {/* Margin (desktop only) */}
                {isDesktop && (
                  <WorkshopHoverCell
                    enabled={enableHoverWorkshop}
                    estimate={estimate}
                    formatCurrency={formatCurrency}
                    className="text-sm cursor-pointer hover:underline decoration-dotted underline-offset-4"
                    style={{ textAlign: "right" }}
                    onClick={() => setLocation(`/quotes/${estimate.id}`)}
                  >
                    {estimate.margin !== undefined && estimate.margin !== null ? (
                      <span className={cn(TONE_TEXT[marginToneOf(estimate.margin, estimate.targetMargin)], "font-medium")}>
                        {estimate.margin.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </WorkshopHoverCell>
                )}

                {/* Status inline dropdown (desktop only) */}
                {isDesktop && (
                  <div style={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    {estimate.status === "converted" ? (
                      <span className={`inline-flex items-center gap-1.5 h-7 text-xs font-medium px-2.5 border rounded-full ${getStatusColor("converted")}`}>
                        {getStatusIcon("converted")}
                        {STATUS_LABELS.converted}
                      </span>
                    ) : (
                      <Select value={estimate.status} onValueChange={(v) => onStatusChange(estimate.id, v)}>
                        <SelectTrigger
                          className={`h-7 text-xs font-medium px-2.5 border rounded-full gap-1.5 ${getStatusColor(estimate.status)}`}
                          style={{ width: "auto", minWidth: "110px" }}
                        >
                          {getStatusIcon(estimate.status)}
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.filter(s => s.value !== "converted").map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Actions ••• dropdown (desktop only) */}
                {isDesktop && (
                  <div style={{ display: "flex", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label="Quote actions" className="h-7 px-2 text-muted-foreground hover:text-foreground">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => setLocation(`/quotes/${estimate.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleExport(estimate.id, "customer")}>
                          <FileText className="w-4 h-4 mr-2 text-blue-500" />
                          Export Customer PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport(estimate.id, "internal")}>
                          <Lock className="w-4 h-4 mr-2 text-primary" />
                          Export Cost Report{!isEmailPro && " (Pro)"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handlePreview(estimate.id, "customer")}>
                          <FileText className="w-4 h-4 mr-2 text-blue-500" />
                          Preview Customer PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handlePreview(estimate.id, "internal")}>
                          <Lock className="w-4 h-4 mr-2 text-primary" />
                          Preview Cost Report{!isEmailPro && " (Pro)"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {isEmailPro ? (
                          <DropdownMenuItem onClick={() => handleOpenSendEmail(estimate)}>
                            <Mail className="w-3.5 h-3.5 mr-2" />
                            Send Email
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem asChild>
                            <Link href="/upgrade" className="flex items-center gap-2 text-muted-foreground">
                              <Lock className="w-3.5 h-3.5 mr-0 text-amber-500" />
                              <Mail className="w-3.5 h-3.5 mr-2" />
                              Send Email
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {estimate.status !== "converted" && (
                          <DropdownMenuItem onClick={() => setConvertConfirmId(estimate.id)}>
                            <Receipt className="w-3.5 h-3.5 mr-2" />
                            Convert to Invoice
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDuplicate(estimate.id)}>
                          <Copy className="w-3.5 h-3.5 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {qboPush.connected && (
                          <>
                            <DropdownMenuSeparator />
                            <QuickBooksPushMenuItem
                              kind="estimate"
                              id={estimate.id}
                              testId={`menu-qbo-push-estimate-${estimate.id}`}
                            />
                          </>
                        )}
                        {xeroPush.connected && (
                          <>
                            <DropdownMenuSeparator />
                            <XeroPushMenuItem
                              kind="estimate"
                              id={estimate.id}
                              testId={`menu-xero-push-estimate-${estimate.id}`}
                            />
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteEstimateId(estimate.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}

                {/* Mobile bottom row (mobile only) */}
                {!isDesktop && (
                  <div
                    className="flex items-center justify-between mt-1 pt-2 border-t border-border/50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedIds.has(estimate.id)}
                        onCheckedChange={() => toggleSelect(estimate.id)}
                        aria-label={`Select quote ${estimate.estimateNumber || estimate.id}`}
                      />
                      {estimate.status === "converted" ? (
                        <span className={`inline-flex items-center gap-1 h-6 text-xs font-medium px-2 border rounded-full ${getStatusColor("converted")}`}>
                          {getStatusIcon("converted")}
                          {STATUS_LABELS.converted}
                        </span>
                      ) : (
                        <Select value={estimate.status} onValueChange={(v) => onStatusChange(estimate.id, v)}>
                          <SelectTrigger className={`h-6 text-xs font-medium px-2 border rounded-full gap-1 ${getStatusColor(estimate.status)}`} style={{ width: "auto" }}>
                            {getStatusIcon(estimate.status)}
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.filter(s => s.value !== "converted").map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {estimate.profit !== undefined && estimate.profit !== null && (
                        <span className={`text-xs font-medium ${estimate.profit < 0 ? "text-red-500" : "text-green-600"}`}>
                          {formatCurrency(estimate.profit)}
                        </span>
                      )}
                      {estimate.margin !== undefined && estimate.margin !== null && (
                        <span className={cn("text-xs font-medium", TONE_TEXT[marginToneOf(estimate.margin, estimate.targetMargin)])}>
                          {estimate.margin.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-sm">{formatCurrency(estimate.total)}</div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Quote actions"
                            className="h-6 px-2 text-muted-foreground hover:text-foreground"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => setLocation(`/quotes/${estimate.id}`)}>
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleExport(estimate.id, "customer")}>
                            <FileText className="w-4 h-4 mr-2 text-blue-500" />
                            Export Customer PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExport(estimate.id, "internal")}>
                            <Lock className="w-4 h-4 mr-2 text-primary" />
                            Export Cost Report{!isEmailPro && " (Pro)"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handlePreview(estimate.id, "customer")}>
                            <FileText className="w-4 h-4 mr-2 text-blue-500" />
                            Preview Customer PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePreview(estimate.id, "internal")}>
                            <Lock className="w-4 h-4 mr-2 text-primary" />
                            Preview Cost Report{!isEmailPro && " (Pro)"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {isEmailPro ? (
                            <DropdownMenuItem onClick={() => handleOpenSendEmail(estimate)}>
                              <Mail className="w-3.5 h-3.5 mr-2" />
                              Send Email
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem asChild>
                              <Link href="/upgrade" className="flex items-center gap-2 text-muted-foreground">
                                <Lock className="w-3.5 h-3.5 mr-0 text-amber-500" />
                                <Mail className="w-3.5 h-3.5 mr-2" />
                                Send Email
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {estimate.status !== "converted" && (
                            <DropdownMenuItem onClick={() => setConvertConfirmId(estimate.id)}>
                              <Receipt className="w-3.5 h-3.5 mr-2" />
                              Convert to Invoice
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDuplicate(estimate.id)}>
                            <Copy className="w-3.5 h-3.5 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {qboPush.connected && (
                            <>
                              <DropdownMenuSeparator />
                              <QuickBooksPushMenuItem
                                kind="estimate"
                                id={estimate.id}
                                testId={`menu-qbo-push-estimate-mobile-${estimate.id}`}
                              />
                            </>
                          )}
                          {xeroPush.connected && (
                            <>
                              <DropdownMenuSeparator />
                              <XeroPushMenuItem
                                kind="estimate"
                                id={estimate.id}
                                testId={`menu-xero-push-estimate-mobile-${estimate.id}`}
                              />
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteEstimateId(estimate.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteEstimateId !== null} onOpenChange={(open) => { if (!open) setDeleteEstimateId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this quote? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteEstimate.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {deleteEstimate.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => { if (!open) setBulkDeleteOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Quote{selectedIds.size !== 1 ? "s" : ""}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} quote{selectedIds.size !== 1 ? "s" : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isBulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ZIP export type picker dialog */}
      <Dialog open={zipDialogOpen} onOpenChange={setZipDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Export {selectedIds.size} Quote{selectedIds.size !== 1 ? "s" : ""} as ZIP</DialogTitle>
            <DialogDescription>
              Choose which PDF type to include in the ZIP archive.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              variant="outline"
              className="justify-start gap-3 h-auto py-3"
              onClick={() => handleBulkExportZip("customer")}
            >
              <FileText className="w-5 h-5 text-blue-500 shrink-0" />
              <div className="text-left">
                <p className="font-medium text-sm">Customer PDF</p>
                <p className="text-xs text-muted-foreground">One PDF per quote, no internal data</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-3 h-auto py-3"
              onClick={() => handleBulkExportZip("internal")}
            >
              <Lock className="w-5 h-5 text-primary shrink-0" />
              <div className="text-left">
                <p className="font-medium text-sm">Cost Report{!isEmailPro && " (Pro)"}</p>
                <p className="text-xs text-muted-foreground">One PDF per quote, full details &amp; costs</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start gap-3 h-auto py-3"
              onClick={() => handleBulkExportZip("both")}
            >
              <Archive className="w-5 h-5 text-purple-500 shrink-0" />
              <div className="text-left">
                <p className="font-medium text-sm">Both PDFs{!isEmailPro && " (Pro)"}</p>
                <p className="text-xs text-muted-foreground">Two PDFs per quote — customer &amp; cost report</p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PDFPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        blobUrl={previewBlobUrl}
        title={previewTitle}
      />

      <MarkAsSentDialog
        open={markAsSentOpen}
        estimateCount={markAsSentIds.length}
        onConfirm={() => {
          setMarkAsSentOpen(false);
          Promise.all(
            markAsSentIds.map(id => updateEstimate.mutateAsync({ id, data: { status: "sent" } }))
          ).then(() => {
            queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
            toast({ title: `${markAsSentIds.length === 1 ? "Quote" : `${markAsSentIds.length} quotes`} marked as Sent` });
          }).catch(() => {
            toast({ title: "Some updates failed", variant: "destructive" });
            queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
          });
        }}
        onSkip={() => setMarkAsSentOpen(false)}
      />

      {/* Send Email dialog */}
      {(() => {
        const sendEmailEstimate = estimates?.find(e => e.id === sendEmailEstimateId) ?? null;
        return (
          <Dialog open={sendEmailEstimateId !== null} onOpenChange={(open) => { if (!open) { setSendEmailEstimateId(null); setCcSelf(true); setAttachServiceAgreement(true); } }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Send Quote via Email
                </DialogTitle>
                <DialogDescription>
                  {sendEmailEstimate?.customerEmail
                    ? <>Sending to <span className="font-medium text-foreground">{sendEmailEstimate.customerEmail}</span></>
                    : <span className="text-destructive">No email on file for this customer. Please add one to their record first.</span>
                  }
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-sm font-medium block mb-1">Subject</label>
                  <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Message</label>
                  <Textarea
                    value={emailMessage}
                    onChange={e => setEmailMessage(e.target.value)}
                    className="min-h-[120px] resize-none text-sm"
                  />
                </div>
                {companySettings?.replyToEmail ? (
                  <div className="flex items-center gap-2">
                    <input
                      id="cc-self-estimate-list"
                      type="checkbox"
                      checked={ccSelf}
                      onChange={e => setCcSelf(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                    />
                    <label htmlFor="cc-self-estimate-list" className="text-sm cursor-pointer select-none">
                      Send a copy to myself <span className="text-muted-foreground">({companySettings.replyToEmail})</span>
                    </label>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    To CC yourself on sent quotes, add a reply-to email in{" "}
                    <a href="/settings" className="underline hover:text-foreground transition-colors">Settings</a>.
                  </p>
                )}
                {companySettings?.serviceAgreementFileName && (
                  <div className="flex items-center gap-2">
                    <input
                      id="attach-service-agreement-list"
                      type="checkbox"
                      checked={attachServiceAgreement}
                      onChange={e => setAttachServiceAgreement(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                    />
                    <label htmlFor="attach-service-agreement-list" className="text-sm cursor-pointer select-none">
                      Attach Service Agreement <span className="text-muted-foreground">({companySettings.serviceAgreementFileName})</span>
                    </label>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSendEmailEstimateId(null); setCcSelf(true); setAttachServiceAgreement(true); }}>Cancel</Button>
                <Button
                  disabled={!sendEmailEstimate?.customerEmail || sendEmailMutation.isPending || !emailSubject || !emailMessage}
                  onClick={async () => {
                    if (!sendEmailEstimate?.customerEmail || !sendEmailEstimateId) return;
                    try {
                      const detail = await queryClient.fetchQuery(getGetEstimateQueryOptions(sendEmailEstimateId));
                      const detailEst = {
                        ...detail,
                        createdAt: String(detail.createdAt),
                      };
                      const attachmentBase64 = customerPDFBase64(detailEst, companySettings ?? {});
                      const attachmentFilename = customerPDFFilename(detailEst);
                      sendEmailMutation.mutate(
                        { id: sendEmailEstimateId, body: { subject: emailSubject, message: emailMessage, attachmentBase64, attachmentFilename, ccSelf, attachServiceAgreement } },
                        {
                          onSuccess: () => {
                            setSendEmailEstimateId(null);
                            setCcSelf(true);
                            setAttachServiceAgreement(true);
                            queryClient.invalidateQueries({ queryKey: getListEstimatesQueryKey() });
                            toast({ title: "Email sent", description: `Quote sent to ${sendEmailEstimate.customerEmail}` });
                          },
                          onError: (err: unknown) => {
                            const msg = err instanceof Error ? err.message : "Failed to send email";
                            toast({ title: "Failed to send", description: msg, variant: "destructive" });
                          }
                        }
                      );
                    } catch {
                      toast({ title: "Failed to load quote details", variant: "destructive" });
                    }
                  }}
                >
                  {sendEmailMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Convert to Invoice confirmation dialog */}
      <AlertDialog open={convertConfirmId !== null} onOpenChange={(open) => { if (!open) setConvertConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert to Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new invoice from this quote and redirect you to it. The quote will be marked as converted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={convertToInvoice.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConvertConfirm}
              disabled={convertToInvoice.isPending}
              className="disabled:opacity-50"
            >
              {convertToInvoice.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Converting…</> : "Convert to Invoice"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={fromTemplateOpen} onOpenChange={o => { if (!isCreatingFromTemplate) setFromTemplateOpen(o); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Quote from Template</DialogTitle>
            <DialogDescription>Pick a template and choose a customer to create a new draft quote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {fromTemplateLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : fromTemplateList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No quote templates saved yet. Open a quote, click ⋮, and choose "Save as Template".
              </div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {fromTemplateList.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFromTemplateSelected(t.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${fromTemplateSelected === t.id ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/40 hover:bg-muted/40"}`}
                  >
                    <div className="font-medium text-sm">{t.name}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
                    <div className="text-xs text-muted-foreground mt-0.5">{t.lineItemCount} line item{t.lineItemCount !== 1 ? "s" : ""}</div>
                  </button>
                ))}
              </div>
            )}

            {fromTemplateSelected !== null && (
              <div>
                <label className="text-sm font-medium block mb-2">Select Customer</label>
                <Popover open={ftCustomerPickerOpen} onOpenChange={setFtCustomerPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      <span className={!ftCustomerName ? "text-muted-foreground" : ""}>{ftCustomerName || "Choose a customer…"}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command>
                      <CommandInput placeholder="Search customers…" value={ftCustomerSearch} onValueChange={setFtCustomerSearch} />
                      <CommandList>
                        <CommandEmpty>No customers found.</CommandEmpty>
                        <CommandGroup>
                          {filteredCustomers.map(c => (
                            <CommandItem key={c.id} value={c.name} onSelect={() => { setFtCustomerId(c.id); setFtCustomerName(c.name); setFtCustomerPickerOpen(false); setFtCustomerSearch(""); }}>
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isCreatingFromTemplate} onClick={() => setFromTemplateOpen(false)}>Cancel</Button>
            <Button disabled={!fromTemplateSelected || !ftCustomerId || isCreatingFromTemplate} onClick={createFromTemplate}>
              {isCreatingFromTemplate ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
